/**
 * Constantes parametrizáveis das calculadoras.
 * Fonte única — nunca hardcode estes valores na UI.
 * (docs/calculamei-spec-calculadoras.md — "Princípios que valem para todas")
 */

/**
 * Teto de faturamento anual do MEI por ano-calendário.
 * - Até 2026: R$ 81.000 (LC 123, art. 18-A, § 1º — valor desde 2018).
 * - 2027: R$ 110.000 (LC 238/2026, art. 2º — regra transitória).
 * - 2028 em diante: R$ 140.000 (LC 238/2026, art. 1º — efeitos a partir de 1º/01/2028).
 */
export const LIMITE_MEI_POR_ANO: Readonly<Record<number, number>> = {
  2026: 81000,
  2027: 110000,
  2028: 140000,
}

/** Anos que as calculadoras oferecem pra simular (o atual e os dois tetos novos). */
export const ANOS_LIMITE_MEI = [2026, 2027, 2028] as const

/** Teto anual do MEI para o ano-calendário informado. */
export function getLimiteMei(ano: number): number {
  if (ano <= 2026) return 81000
  if (ano === 2027) return 110000
  return 140000
}

/**
 * Teto proporcional por mês — também é o limite por mês de atividade de quem
 * abre o MEI no meio do ano (81.000/12 = 6.750; 2027: 9.166,67; 2028: 11.666,67).
 */
export function getLimiteMeiMes(ano: number): number {
  return Number((getLimiteMei(ano) / 12).toFixed(2))
}

/**
 * Excesso tolerado: quem passa do teto em até 20% só é desenquadrado em
 * janeiro do ano seguinte; acima disso, o desenquadramento retroage a janeiro
 * do próprio ano (LC 123, art. 18-A, § 7º, III — não alterado pela LC 238/2026).
 */
export const TOLERANCIA_EXCESSO_MEI = 0.2

export function getLimiteMeiComTolerancia(ano: number): number {
  return getLimiteMei(ano) * (1 + TOLERANCIA_EXCESSO_MEI)
}

/** Ano-calendário corrente (no build de páginas estáticas, o ano do deploy). */
export const LIMITE_MEI_ANO_REFERENCIA = Math.max(new Date().getFullYear(), 2026)

/** Teto de faturamento anual do MEI no ano corrente. */
export const LIMITE_MEI_ANO = getLimiteMei(LIMITE_MEI_ANO_REFERENCIA)

/** Teto proporcional por mês no ano corrente (2026: 81.000 / 12 = 6.750). */
export const LIMITE_MEI_MES = LIMITE_MEI_ANO / 12

/** Próximo teto já definido em lei — usado nos avisos "o que muda em 2027". */
export const PROXIMO_LIMITE_MEI = {
  ano: LIMITE_MEI_ANO_REFERENCIA + 1,
  valor: getLimiteMei(LIMITE_MEI_ANO_REFERENCIA + 1),
}

/**
 * Semanas por mês — constante de conversão de jornada.
 * Usada em preço por hora: horasMes = horasDia × diasSemana × SEMANAS_MES.
 */
export const SEMANAS_MES = 4.33
